import { Duration, Factory } from '..'
import { Price } from './price-cap'
import {
  applyEntitySchema,
  objectSchema,
  openUnion,
  optionalTolerant,
  parseEntity,
  sourceJson,
} from '../../_utils/fcdc-v2'
import * as z from 'zod'

export enum ExpiryReminderType {
  Free = 'Free',
  Paid = 'Paid',
}

const ExpiryReminderSchema = openUnion('type', {
  [ExpiryReminderType.Free]: objectSchema({
    type: z.literal(ExpiryReminderType.Free),
    startSendingAt: optionalTolerant(z.union([z.string(), z.number()])),
    durationUntilExpiry: optionalTolerant(z.unknown()),
    sentAt: optionalTolerant(z.union([z.string(), z.number()])),
  }),
  [ExpiryReminderType.Paid]: objectSchema({
    type: z.literal(ExpiryReminderType.Paid),
    startSendingAt: optionalTolerant(z.union([z.string(), z.number()])),
    durationUntilExpiry: optionalTolerant(z.unknown()),
    sentAt: optionalTolerant(z.union([z.string(), z.number()])),
    prices: optionalTolerant(z.array(z.unknown())),
  }),
})

export class ExpiryReminder {
  type: ExpiryReminderType
  startSendingAt: Date
  durationUntilExpiry: Duration
  sentAt?: Date

  constructor(json: any) {
    const source = sourceJson(json) as Record<string, unknown>
    this.type = source['type'] as ExpiryReminderType
    this.startSendingAt = new Date(source['startSendingAt'] as string)
    this.durationUntilExpiry = source['durationUntilExpiry'] as Duration
    this.sentAt = source['sentAt'] ? new Date(source['sentAt'] as string) : undefined
    applyEntitySchema(this, ExpiryReminderSchema, source, {})
  }

  static getFactory(): Factory<ExpiryReminder> {
    return new (class implements Factory<ExpiryReminder> {
      make(json: any): ExpiryReminder {
        if (json?.type === ExpiryReminderType.Free) {
          return new FreeExpiryReminder(json)
        }
        if (json?.type === ExpiryReminderType.Paid && Array.isArray(json?.prices)) {
          return new PaidExpiryReminder(json)
        }
        return parseEntity(ExpiryReminderSchema, json ?? {}) as unknown as ExpiryReminder
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on an ExpiryReminder. ExpiryReminders are not stored in IndexDB.'
        )
      }
    })()
  }
}

export class FreeExpiryReminder extends ExpiryReminder {
  constructor(json: any) {
    super(json)
  }
}

export class PaidExpiryReminder extends ExpiryReminder {
  prices?: Price[]
  constructor(json: any) {
    const source = sourceJson(json) as Record<string, unknown>
    const prices = Array.isArray(source['prices'])
      ? source['prices'].map(price => sourceJson(price))
      : undefined
    super(Array.isArray(prices) ? { ...source, prices } : source)
    this.prices = prices?.map(price => new Price(price))
  }
}
